import { styled } from "@mui/material/styles";
import { black } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import FlexRow from "designSystem/components/flex/FlexRow";
import { Box, ChipArray, MultiSelect, SingleSelect, Text } from "designSystem/index";
import ArrayDataStore from "csi/datastore/ArrayDataStore";
import React, { FunctionComponent, ReactNode, useState } from "react";
import Query from "csi/xml/Query";

const PREFIX = "MultiFilter";

const classes = {
	filter: `${PREFIX}filter`,
	filterChips: `${PREFIX}filterChips`,
	filterSelectionChip: `${PREFIX}filterSelectionChip`,
};

const StyledBox = styled(Box)((
	{
		theme: Theme,
	},
) => ({
	[`& .${classes.filter}`]: {
		marginLeft: 12,
		marginRight: 12,
		alignItems: "center",
		"& >div": {
			margin: "6px 3px",
			flexGrow: 1,
			flexBasis: 0,
			"& >div": {
				width: "100%",
			},
		},
	},

	[`& .${classes.filterChips}`]: {
		marginLeft: 12,
		marginRight: 12,
	},

	[`& .${classes.filterSelectionChip}`]: {
		fontSize: 12,
		padding: 3,
		color: black[100],
		"& .MuiTooltip-tooltip": {
			color: "unset",
		},
	},
}));

// one row of filter params
// corresponds to one value in first select and corresponding second select data, current selection and more
export interface FilterData {
	id: string;
	name: string;
	type?: string;
	// store to fill second select drop down
	store: ArrayDataStore<ComponentItem>;
	// server request query filter
	applyQueryFilter: (query: Query) => void;
	// service file function return true if conditions require to disable second select
	disableMultiSelect?: () => boolean;
	// second filter control to use instead of multiselect
	filterControl: (onChangeSelection) => ReactNode;
	// curent selection, is nulll if no current selection
	selection: ComponentItem[];
}

// main structure which holds select data (stores), event handlers, selection and more
export interface FilterParams {
	filterData: FilterData[];
	// service file callback to be called on filter values (Second Select) change
	callbackOnChangeSelection?: (selectedFilter?: number, newValue?: any) => void;
	// service file callback to be called when user delete on of the chips
	callbackOnChipDelete?: (item: ComponentItem) => void;
	chipItems: ComponentItem[];
	selectedFilterItem: number;
}

// component parametes to be supplised by prent
export interface FilterProps {
	// parent callback to be called when filter change requre parent update
	onChange: () => void;
	filterParams: FilterParams;
}

// Dummy store for empty filter, create as static
const multiselectStoreEmpty = new ArrayDataStore<ComponentItem>([], "name", null);

export const ComplianceAnalysisMultiFilter: FunctionComponent<FilterProps> = (props) => {
	const { filterParams, onChange } = props;
	const { callbackOnChangeSelection, callbackOnChipDelete, filterData, selectedFilterItem } = filterParams;
	const [selectedFilter, setSelectedFilter] = useState(selectedFilterItem);

	// pick up existing selection and update chips
	const updateChipItems = () => {
		const chipItems = [];
		filterData.forEach((item) => {
			if (item.selection.length) {
				const itemSelectionMap = item.selection.map((i) => i.name).join(", ");
				const chipName = item.name + ":";
				chipItems.push({
					id: item.id,
					name: (
						<span>
							<Text
								bold
								className={classes.filterSelectionChip + " csi-no-tooltip-colors"}
								text={chipName}
							/>
							{itemSelectionMap}
						</span>
					),
				});
			}
		});

		return filterParams.chipItems = chipItems;
	};
	const [items, setItems] = useState(updateChipItems());

	// filter Type (First Select) onChange
	const onChangeFilterSelection = (event, newValue, reason) => {
		const selectedIndex = filterData.indexOf(newValue);
		filterParams.selectedFilterItem = selectedIndex;
		setSelectedFilter(selectedIndex);
	};

	// filter values (Second Select) onChange
	const onChangeSelection = (event, newValue, reason) => {
		filterData[selectedFilter].selection = newValue;
		callbackOnChangeSelection?.(selectedFilter, newValue);
		setItems(updateChipItems());
		updateParent();
	};

	// update filter data on Chip delete
	const onChipDelete = (item) => {
		const arr = filterParams.chipItems;
		arr.splice(arr.indexOf(item), 1);
		filterData.filter((i) => i.id == item.id)[0].selection = [];
		callbackOnChipDelete && callbackOnChipDelete(item);
		updateParent();
	};

	// call parent's onChange callback
	const updateParent = () => {
		onChange();
	};

	const selectedFilterData = filterData[selectedFilter];
	const disableMultiselect = selectedFilterData.disableMultiSelect();
	const { filterControl } = selectedFilterData;

	return (
		<StyledBox>
			<FlexColumn>
				<FlexRow className={classes.filter}>
					<Text
						res="Filter"
					/>:
					<SingleSelect
						onChange={onChangeFilterSelection}
						options={filterData}
						required
						value={selectedFilterData}
					/>
					{filterControl?.(onChangeSelection) || (
						<MultiSelect
							allowCustomOptions
							disableClearable
							disabled={disableMultiselect}
							filterLimit={250}
							hideNoResults
							noChips
							onChange={onChangeSelection}
							placeHolderRes="Select"
							store={disableMultiselect ? multiselectStoreEmpty : selectedFilterData.store}
							value={disableMultiselect ? [] : selectedFilterData.selection}
						/>
					)}
				</FlexRow>
				{items && (
					<Box className={classes.filterChips}>
						<ChipArray<ComponentItem>
							chipMaxWidth={275}
							items={items}
							name=""
							onDeleteItem={onChipDelete}
							variant="default"
						/>
					</Box>
				)}
			</FlexColumn>
		</StyledBox>
	);
};

export default ComplianceAnalysisMultiFilter;